import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPerformanceSummary } from "../../../slices/Employee/employeePerformanceSummarySlice";

import "./StackedColumnStyles.scss";
import "../ChartFrame.scss";
import ChartTooltip from "../ChartTooltip";
import { showError } from "../../../helpers/Notifications/Notification";
import { STATUS } from "../chartPalette";
import { CHART_THEME } from "../chartTheme";
import { linearScale, niceAxis, wrapLabel } from "../svg/helpers";

const STACKS = [
  {
    key: "Yes",
    name: "Practices Performed",
    top: STATUS.yes.top,
    base: STATUS.yes.base,
  },
  {
    key: "Incomplete",
    name: "Practices Incompletely Performed",
    top: STATUS.incomplete.top,
    base: STATUS.incomplete.base,
  },
  {
    key: "No",
    name: "Practices Not Performed",
    top: STATUS.no.top,
    base: STATUS.no.base,
  },
];

export default function StackedColumn({
  domains,
  selectedSurvey,
  isAdmin = false,
}) {
  const performanceSummary = useSelector((state) => state.performanceSummary);
  const dispatch = useDispatch();
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (!domains) return;
    fetchPerformanceSummary();
  }, [domains]);

  const fetchPerformanceSummary = useCallback(() => {
    if (isAdmin && !selectedSurvey) return;
    dispatch(getPerformanceSummary(selectedSurvey))
      .then((data) => {
        oraganizeData(domains, data.payload?.data ?? []);
      })
      .catch(() => {});
  }, [domains, selectedSurvey]);

  const oraganizeData = (domainsList, performaceData) => {
    try {
      const organizedPerformanceData = performaceData.map((p) => {
        const domain = domainsList.find((d) => d.id === p.domain_id);
        return { ...p, domain: domain?.domain || "" };
      });
      setPerformanceMetrics(organizedPerformanceData);
    } catch (err) {
      showError("Something went wrong");
    }
  };

  const columns = useMemo(() => {
    return performanceMetrics.map((p) => {
      const yes = parseInt(p.Yes, 10) || 0;
      const incomplete = parseInt(p.Incomplete, 10) || 0;
      const no = parseInt(p.No, 10) || 0;
      return {
        domain: p.domain,
        yes,
        incomplete,
        no,
        total: yes + incomplete + no,
        segments: [
          { key: "Yes", value: yes, ...STACKS[0] },
          { key: "Incomplete", value: incomplete, ...STACKS[1] },
          { key: "No", value: no, ...STACKS[2] },
        ],
      };
    });
  }, [performanceMetrics]);

  const width = 1000;
  const height = 300;
  const margin = { top: 24, right: 16, bottom: 72, left: 36 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  // Nice, round y-ticks from the data max (also avoids duplicate tick values —
  // the old rounding produced repeated keys when the max was small).
  const rawMax = Math.max(1, ...columns.map((c) => c.total));
  const { max: maxY, ticks: yTicks } = niceAxis(rawMax);
  const yScale = linearScale(0, maxY, plotH, 0);
  const colGap = 10;
  const colW =
    columns.length > 0
      ? Math.max(18, (plotW - colGap * (columns.length - 1)) / columns.length)
      : 24;

  return (
    <div className="stacked-column stacked-column--panel cb-chart-frame cb-chart-frame--stack">
      <div className="plotting-details">
        {STACKS.map((s) => (
          <div key={s.key} className="text-container">
            <div
              className={
                s.key === "Yes"
                  ? "practices-performed-icon"
                  : s.key === "Incomplete"
                  ? "practices-incompletely-performed-icon"
                  : "practices-not-performed-icon"
              }
            />
            <p>{s.name}</p>
          </div>
        ))}
      </div>

      <div className="stacked-column-container">
        {!performanceSummary.loading && domains && (
          <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Performance summary by domain"
          >
            <g transform={`translate(${margin.left},${margin.top})`}>
              {yTicks.map((t, ti) => (
                <g key={ti}>
                  <line
                    x1={0}
                    x2={plotW}
                    y1={yScale(t)}
                    y2={yScale(t)}
                    stroke={CHART_THEME.grid}
                    strokeDasharray="4 6"
                  />
                  <text
                    className="cb-chart-frame__axis-label"
                    x={-8}
                    y={yScale(t) + 4}
                    textAnchor="end"
                  >
                    {t}
                  </text>
                </g>
              ))}

              {columns.map((col, i) => {
                const x = i * (colW + colGap);
                let yCursor = plotH;
                return (
                  <g key={col.domain + i}>
                    {col.segments.map((seg) => {
                      if (!seg.value) return null;
                      const segH = plotH - yScale(seg.value);
                      yCursor -= segH;
                      const rectY = yCursor;
                      return (
                        <rect
                          key={seg.key}
                          x={x}
                          y={rectY}
                          width={colW}
                          height={segH}
                          style={{ fill: seg.base, cursor: "pointer" }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget
                              .closest(".cb-chart-frame")
                              ?.getBoundingClientRect();
                            setHover({
                              x: e.clientX - (rect?.left || 0),
                              y: e.clientY - (rect?.top || 0),
                              domain: col.domain,
                              segments: col.segments.filter((s) => s.value > 0),
                            });
                          }}
                          onMouseLeave={() => setHover(null)}
                        />
                      );
                    })}
                    {col.total > 0 && (
                      <text
                        className="cb-chart-frame__tick-label"
                        x={x + colW / 2}
                        y={yScale(col.total) - 8}
                        textAnchor="middle"
                        style={{ fontWeight: 700, fill: CHART_THEME.secondary }}
                      >
                        {col.total}
                      </text>
                    )}
                    <text
                      className="cb-chart-frame__tick-label"
                      x={x + colW / 2}
                      y={plotH + 16}
                      textAnchor="middle"
                    >
                      {wrapLabel(col.domain, 12).map((ln, li) => (
                        <tspan
                          key={li}
                          x={x + colW / 2}
                          dy={li === 0 ? 0 : 11}
                        >
                          {ln}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>
      <p className="stacked-column-footer">Domains</p>

      <ChartTooltip x={hover?.x || 0} y={hover?.y || 0} visible={!!hover}>
        {hover && (
          <>
            <span className="cb-chart-tooltip__title">{hover.domain}</span>
            {hover.segments.map((s) => (
              <span key={s.key} className="cb-chart-tooltip__row">
                <span
                  className="cb-chart-tooltip__dot"
                  style={{ background: s.base }}
                />
                {s.name}: <strong>{s.value}</strong>
              </span>
            ))}
          </>
        )}
      </ChartTooltip>
    </div>
  );
}
